export const agoFromNow = (
  date: string,
  t?: (key: string, options?: any) => string
): string => {
  const now = new Date();
  const pastDate = new Date(date);
  const diffInMs = now.getTime() - pastDate.getTime();

  // Convert to appropriate time units
  const seconds = Math.floor(diffInMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  // If translation function is provided, use it
  if (t) {
    if (years > 0) {
      return `${years} ${t(years === 1 ? "common.time_ago.years.one" : "common.time_ago.years.other", { count: years })}`;
    } else if (months > 0) {
      return `${months} ${t(months === 1 ? "common.time_ago.months.one" : "common.time_ago.months.other", { count: months })}`;
    } else if (days > 0) {
      return `${days} ${t(days === 1 ? "common.time_ago.days.one" : "common.time_ago.days.other", { count: days })}`;
    } else if (hours > 0) {
      return `${hours} ${t(hours === 1 ? "common.time_ago.hours.one" : "common.time_ago.hours.other", { count: hours })}`;
    } else if (minutes > 0) {
      return `${minutes} ${t(minutes === 1 ? "common.time_ago.minutes.one" : "common.time_ago.minutes.other", { count: minutes })}`;
    } else {
      return seconds <= 1
        ? t("common.time_ago.just_now")
        : `${seconds} ${t(seconds === 1 ? "common.time_ago.seconds.one" : "common.time_ago.seconds.other", { count: seconds })}`;
    }
  }

  // Fallback to English if no translation function is provided
  if (years > 0) {
    return years === 1 ? "1 year ago" : `${years} years ago`;
  } else if (months > 0) {
    return months === 1 ? "1 month ago" : `${months} months ago`;
  } else if (days > 0) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  } else if (hours > 0) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  } else if (minutes > 0) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  } else {
    return seconds <= 1 ? "just now" : `${seconds} seconds ago`;
  }
};

export function sortByDate<T>(
  array: T[],
  dateField: keyof T,
  order: "asc" | "desc" = "desc"
): T[] {
  return [...array].sort((a, b) => {
    const dateA = new Date(a[dateField] as string).getTime();
    const dateB = new Date(b[dateField] as string).getTime();
    return order === "desc" ? dateB - dateA : dateA - dateB;
  });
}

export const formatDate = (
  date: Date | string,
  locale: string = "en-GB"
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export function formatNumberString(val: any) {
  if (val === undefined || val === null || isNaN(val)) return "0";
  const str = typeof val === "number" ? val.toString() : val;
  if (!str.includes(".")) return str; // No dot, return as is
  const [intPart, decimals = ""] = str.split(".");
  if (/^0+$/.test(decimals)) return intPart; // All zeros after dot
  if (decimals.length === 0) return str; // Has dot but no decimals
  if (decimals.length <= 2) return str; // 1 or 2 decimals, return as is
  // More than 2 decimals, trim to 2 (no rounding)
  return `${intPart}.${decimals.slice(0, 2)}`;
}

export const utils = {
  normalizeDate: (date: Date): Date => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const normalized = new Date(
      date.toLocaleString("en-US", { timeZone: userTimezone })
    );
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  },

  computeDaysDifference: (date1: Date, date2: Date): number =>
    Math.floor((date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24)),

  computeStreak: (
    today: Date,
    lastCheckIn: Date,
    checkInStreakValue: number
  ): number => {
    const daysDiff = utils.computeDaysDifference(today, lastCheckIn);
    return daysDiff > 1 ? 0 : checkInStreakValue;
  },
};

export function formatMoneyShort(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  } else {
    return num.toString();
  }
}

export const shortenAddress = (
    address: string,
    first: number = 4,
    last: number = 4
) => {
    if (!address) return "";
    return `${address.substring(0, first)}...${address.substring(address.length - last)}`;
};

